import { LumiDB, type TilesetTreeNode } from "@lumidb/lumidb";
import * as THREE from "three";
import { CSS2DObject } from "three/examples/jsm/renderers/CSS2DRenderer.js";
import { proxy } from "valtio";
import type { App } from "./app";
import { getCachedColor } from "./colors";
import { CrossSection } from "./cross-section";
import { PointChunk } from "./point-chunk";
import { CustomPointMaterial } from "./point-material";
import type { Prefs } from "./prefs";
import { PriorityQueue } from "./priority-queue";
import { getDisplayName, throttle } from "./utils";
import type { Project } from "./project";
import { FOV_RAD, LAYERS, PointHit, Viewer } from "./viewer";

const RETRY_LIMIT = 4;
const PARALLEL_FETCHES = 6;
const EVAL_INTERVAL_MS = 100;
const PICK_WINDOW = 21;
const SWEEP_INTERVAL_MS = 5000;
const MIN_ROOT_TILE_SIZE = 50;
const CROSS_SECTION_MIN_DEPTH = 3;
const MAX_LABEL_COUNT = 5;
const LABEL_PADDING = 40; // padding around the label in pixels
const LABEL_CHAR_WIDTH = 6; // approximate width of a character in pixels based on xs font size

// Tabler map pin icon
const PIN_SVG = `<svg
  xmlns="http://www.w3.org/2000/svg"
  width="14"
  height="14"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
  class="icon icon-tabler icons-tabler-outline icon-tabler-map-pin"
>
  <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
  <path d="M9 11a3 3 0 1 0 6 0a3 3 0 0 0 -6 0" />
  <path d="M17.657 16.657l-4.243 4.243a2 2 0 0 1 -2.827 0l-4.244 -4.243a8 8 0 1 1 11.314 0z" />
</svg>`;

function getBudget(rendering: number, memory: number, minTileSize: number) {
    return {
        rendering: rendering,
        memory: memory,
        minTileSize: minTileSize,
    };
}

const BUDGETS = {
    high: getBudget(10_000_000, 20_000_000, 384),
    medium: getBudget(7_500_000, 15_000_000, 448),
    low: getBudget(5_000_000, 10_000_000, 512),
    crossSection: getBudget(5_000_000, 10_000_000, 64),
} as const;

type Label = {
    tableName: string[];
    position: THREE.Vector3;
    screenPos: THREE.Vector2;
    proj: string;
};

export type TableInfo = {
    datasetId: string;
    tableName: string;
    proj: string;
    root: LoadedTile;
    contentBounds: THREE.Box3;
    position: THREE.Vector3;
    tableGroup: THREE.Group;
    octreeBounds: THREE.Box3;
    externalTrees: Map<string, TilesetTreeNode>;
    material: CustomPointMaterial;
    abort: AbortController;
};

export type TileInfo = {
    id: string;
    tableName: string;
    node: TilesetTreeNode;
    bounds: THREE.Box3;
    depth: number;
    children: TileInfo[] | null;
    data: null | {
        pointCount: number;
        points: THREE.Points<THREE.BufferGeometry<THREE.NormalBufferAttributes>, CustomPointMaterial>;
    };
};

export type LoadedTile = TileInfo & {
    data: {
        pointCount: number;
        points: THREE.Points<THREE.BufferGeometry<THREE.NormalBufferAttributes>, CustomPointMaterial>;
    };
};

// Vertex coordinates relative to Points center is better for floating point precision
function positionPoints(points: THREE.Points) {
    const geometry = points.geometry;
    const attr = geometry.getAttribute("position");
    if (!attr || attr.count === 0) return;

    geometry.computeBoundingBox();
    const box = geometry.boundingBox;
    if (!box) return;

    const center = box.getCenter(new THREE.Vector3());
    const array = attr.array as Float32Array;

    for (let i = 0; i < array.length; i += 3) {
        array[i] -= center.x;
        array[i + 1] -= center.y;
        array[i + 2] -= center.z;
    }

    attr.needsUpdate = true;
    box.min.sub(center);
    box.max.sub(center);
    geometry.computeBoundingSphere();
    points.position.copy(center);
}

function getBvolBox(region: number[], offset: THREE.Vector3Like | null): THREE.Box3 {
    const min = new THREE.Vector3(region[0], region[1], region[4]);
    const max = new THREE.Vector3(region[2], region[3], region[5]);

    if (offset) {
        min.sub(offset);
        max.sub(offset);
    }

    return new THREE.Box3(min, max);
}

export class LOD {
    public state = proxy({
        fetching: 0,
        loaded: 0,
        queued: 0,
        tables: 0,
    });

    public group = new THREE.Group();
    public labels = new THREE.Group();
    public tableInfos = new Map<string, TableInfo>();

    private queuedTiles = new Set<string>();
    private loadedTiles = new Set<LoadedTile>();
    private failedTiles = new Map<string, number>();
    private failedTilesets = new Map<string, number>();
    private startedTables = new Set<string>();
    private tableAborts = new Map<string, AbortController>();
    private evalTimeoutHandle: number | undefined = undefined;
    private currentlyFetching = 0;
    private hitsBuffer: LoadedTile[] = [];
    private evalAt = performance.now();

    private pickRenderTarget: THREE.WebGLRenderTarget;
    private pickMaterials: CustomPointMaterial[];
    private raycaster = new THREE.Raycaster();
    private rgba = new Uint8Array(4);
    private pickBuffer = new Uint8Array(4 * PICK_WINDOW * PICK_WINDOW);
    private pickPosition = new THREE.Vector3();

    private temp = {
        clampedPoint: new THREE.Vector3(),
        frustum: new THREE.Frustum(),
        projectionMatrix: new THREE.Matrix4(),
    };

    private walkQueue = new PriorityQueue<TileInfo | LoadedTile>((it) => this.getTileScreenSize(it));
    private loadQueue = new PriorityQueue<TileInfo>((it) => this.getTileScreenSize(it));
    private labelCache = new Map<string, CSS2DObject>();

    private readonly app: App;
    private readonly lumidb: LumiDB;
    private readonly context: CrossSection | Viewer;
    private readonly prefs: Prefs;
    private readonly project?: Project;

    constructor(context: CrossSection | Viewer, app: App, prefs: Prefs, lumidb: LumiDB, project?: Project) {
        this.context = context;
        this.app = app;
        this.prefs = prefs;
        this.lumidb = lumidb;
        this.project = project;

        context.scene.add(this.group);
        context.labels.add(this.labels);

        this.pickRenderTarget = new THREE.WebGLRenderTarget(PICK_WINDOW, PICK_WINDOW, {
            format: THREE.RGBAFormat,
            minFilter: THREE.NearestFilter,
            magFilter: THREE.NearestFilter,
        });

        this.pickMaterials = Array.from(
            { length: 255 },
            (_, i) => new CustomPointMaterial({ datasetColor: [0, 0, 0], pick: { nodeIndex: i + 1 } }),
        );

        setInterval(() => {
            this.sweep();
        }, SWEEP_INTERVAL_MS);
    }

    public eval(): Promise<unknown> {
        const dt = performance.now() - this.evalAt;
        const { promise, resolve, reject } = Promise.withResolvers();

        clearTimeout(this.evalTimeoutHandle);

        if (dt > EVAL_INTERVAL_MS) {
            this.doEval().then(resolve).catch(reject);
        } else {
            this.evalTimeoutHandle = window.setTimeout(() => {
                this.doEval().then(resolve).catch(reject);
            }, EVAL_INTERVAL_MS - dt);
        }

        return promise;
    }

    public isLoading(): boolean {
        return this.loadQueue.size > 0 || this.currentlyFetching > 0;
    }

    public getTableInfo(tableName: string) {
        return this.tableInfos.get(tableName);
    }

    public intersectTiles(ray: THREE.Ray) {
        this.hitsBuffer.length = 0;

        const queue: TileInfo[] = Array.from(this.tableInfos.values()).map((t) => t.root);

        while (true) {
            const tile = queue.shift();

            if (!tile) {
                break;
            }

            if (ray.intersectsBox(tile.bounds)) {
                if (tile.data) {
                    this.hitsBuffer.push(tile as LoadedTile);
                }

                if (tile.children) {
                    queue.push(...tile.children);
                }
            }
        }

        return this.hitsBuffer;
    }

    public pickPoint(position: THREE.Vector2): PointHit | null {
        if (!this.context.renderer) {
            return null;
        }

        this.raycaster.setFromCamera(position, this.context.camera);
        this.raycaster.layers.set(LAYERS.lod);

        const { width, height } = this.context.size;
        const ray = this.raycaster.ray;
        const tileHits = this.intersectTiles(ray);

        for (const [nodeIdx, tile] of tileHits.entries()) {
            if (nodeIdx < 255) {
                const originalMaterial = tile.data.points.material;
                const pickMaterial = this.pickMaterials[nodeIdx];

                if (originalMaterial instanceof CustomPointMaterial) {
                    pickMaterial.copyVisibilitySettings(originalMaterial);
                }

                tile.data.points.material = pickMaterial;
            }
        }

        const canvasX = ((position.x + 1) / 2) * width;
        const canvasY = ((1 - position.y) / 2) * height;
        const windowX = canvasX - PICK_WINDOW / 2;
        const windowY = canvasY - PICK_WINDOW / 2;

        this.context.camera.layers.set(LAYERS.lod);
        this.context.camera.setViewOffset(width, height, windowX, windowY, PICK_WINDOW, PICK_WINDOW);
        this.context.renderer.setRenderTarget(this.pickRenderTarget);
        this.context.renderer.render(this.context.scene, this.context.camera);
        this.context.renderer.readRenderTargetPixels(
            this.pickRenderTarget,
            0,
            0,
            PICK_WINDOW,
            PICK_WINDOW,
            this.pickBuffer,
        );

        // Reset scene, camera, and color mode
        this.context.camera.clearViewOffset();
        this.context.renderer.setRenderTarget(null);

        for (const tile of tileHits) {
            const tableInfo = this.getTableInfo(tile.tableName);
            if (tableInfo) {
                tile.data.points.material = tableInfo.material;
            }
        }

        let hit = false;
        let closestDist = Infinity;

        // Find closest pixel rgba
        for (let i = 0; i < this.pickBuffer.length / 4; i++) {
            const x = i % PICK_WINDOW;
            const y = Math.floor(i / PICK_WINDOW);
            const sqdist = (x - PICK_WINDOW / 2) ** 2 + (y - PICK_WINDOW / 2) ** 2;

            const r = this.pickBuffer[i * 4 + 0];
            const g = this.pickBuffer[i * 4 + 1];
            const b = this.pickBuffer[i * 4 + 2];
            const a = this.pickBuffer[i * 4 + 3];

            if ((r || g || b) && a > 0) {
                hit = true;
                if (sqdist < closestDist) {
                    this.rgba[0] = r;
                    this.rgba[1] = g;
                    this.rgba[2] = b;
                    this.rgba[3] = a;
                    closestDist = sqdist;
                }
            }
        }

        if (hit) {
            const pointIdx = (this.rgba[0] << 16) | (this.rgba[1] << 8) | this.rgba[2];
            const nodeIndex = this.rgba[3] - 1;
            const tile = tileHits[nodeIndex];

            if (tile?.data) {
                const attrs = tile.data.points.geometry.getAttribute("position");

                if (attrs) {
                    const maxIndex = attrs.array.length / 3;

                    if (pointIdx < maxIndex) {
                        this.pickPosition.fromArray(attrs.array.subarray(pointIdx * 3, pointIdx * 3 + 3));

                        // Vertices are relative to Points center, which  is relative to the table center
                        this.pickPosition.add(tile.data.points.position);
                        const tableInfo = this.getTableInfo(tile.tableName);
                        if (tableInfo) {
                            this.pickPosition.add(tableInfo.tableGroup.position);
                        }

                        return { type: "point", position: this.pickPosition, tile };
                    } else {
                        console.warn(`Index ${pointIdx} out of bounds (max: ${maxIndex - 1})`);
                    }
                }
            }
        }

        return null;
    }

    private errorReferencePoint = new THREE.Vector3();
    private getTileScreenSize(tile: TileInfo): number {
        const camera = this.context.camera;
        const diameter = tile.bounds.min.distanceTo(tile.bounds.max);
        const radius = diameter * 0.5;

        if (camera instanceof THREE.OrthographicCamera) {
            const viewHeight = camera.top - camera.bottom;
            return diameter * (this.context.size.height / viewHeight);
        }

        tile.bounds.clampPoint(camera.position, this.errorReferencePoint);
        const distance = Math.max(radius, camera.position.distanceTo(this.errorReferencePoint));
        const pixelsPerUnitAtDist1 = this.context.size.height / (2 * Math.tan(FOV_RAD / 2));

        return (diameter / distance) * pixelsPerUnitAtDist1;
    }

    private getTileInfo(tableInfo: TableInfo, node: TilesetTreeNode, depth: number): TileInfo {
        return {
            id: `${tableInfo.tableName}/${node.tileId}`,
            tableName: tableInfo.tableName,
            node: node,
            bounds: getBvolBox(node.boundingVolume.region, this.app.state.offset),
            depth,
            children: null,
            data: null,
        };
    }

    public updateMaterials(clipPolygon: [number, number][] | null = null) {
        for (const tableInfo of this.tableInfos.values()) {
            const compare = this.app.state.compare;

            let split = this.app.state.split;
            if (split !== -1.0) {
                if (!compare.includes(tableInfo.datasetId)) {
                    split = -split;
                }
            }

            const dataset = this.app.state.pointclouds[tableInfo.datasetId];
            // The metadata range is in the dataset's unit, the hag attribute in scene units.
            const hagScale = dataset ? this.app.toSceneLength(dataset) : 1;

            tableInfo.material.update({
                // TODO: Should the range be for all datasets?
                hagRange: dataset ? [dataset.hag_range[0] * hagScale, dataset.hag_range[1] * hagScale] : [0, 0],
                hagFilterRange: this.app.state.hagFilterRange,
                zRange: this.app.state.zRange,
                elevationRange: this.app.state.elevationRange,
                pointSize: this.prefs.state.pointSize.value[0],
                filteredPoseIDs: new Set(),
                objectIdMask: this.app.state.objectIdMask,
                colorMode: this.app.state.colorMode,
                classificationMask: this.app.state.classificationMask,
                split: split,
                zOffset: this.app.state.offset.z,
                clipPolygon: clipPolygon,
            });
        }
    }

    private updateStats = throttle(() => {
        this.state.fetching = this.currentlyFetching;
        this.state.queued = this.loadQueue.size;
        this.state.loaded = this.loadedTiles.size;
    });

    private loadTiles() {
        this.loadQueue.reprioritize();

        while (this.currentlyFetching < PARALLEL_FETCHES) {
            const it = this.loadQueue.dequeue();
            if (!it) break;

            const tableInfo = this.tableInfos.get(it.tableName);

            if (!tableInfo) {
                continue;
            }

            this.currentlyFetching++;

            if (it.node.children) {
                PointChunk.loadTile(
                    this.lumidb,
                    it.tableName,
                    it.node.content,
                    tableInfo.material,
                    tableInfo.abort.signal,
                )
                    .then((tileData) => {
                        // The table might already have been unloaded
                        if (tableInfo.abort.signal.aborted || !this.tableInfos.has(it.tableName)) {
                            tileData.points.geometry.dispose();
                            return;
                        }

                        positionPoints(tileData.points);
                        it.data = { pointCount: tileData.pointCount, points: tileData.points };
                        it.data.points.visible = false;
                        tableInfo.tableGroup.add(tileData.points);
                        this.loadedTiles.add(it as LoadedTile);
                        this.eval();
                    })
                    .catch(() => {
                        if (tableInfo.abort.signal.aborted) {
                            return;
                        }

                        const failCount = this.failedTiles.get(it.id);

                        if (failCount === undefined) {
                            this.failedTiles.set(it.id, 1);
                        } else {
                            this.failedTiles.set(it.id, failCount + 1);
                        }
                    })
                    .finally(() => {
                        this.currentlyFetching--;
                        this.queuedTiles.delete(it.id);
                    });
            } else {
                const tableInfo = this.tableInfos.get(it.tableName);
                const failCount = this.failedTilesets.get(it.id) || 0;

                if (!tableInfo || failCount >= RETRY_LIMIT) {
                    this.currentlyFetching--;
                    continue;
                }

                this.lumidb
                    .get3DTileset({
                        uri: it.node.content.uri,
                        tableName: it.tableName,
                        output_format: "json",
                        outputProj: this.app.state.proj,
                        abortSignal: tableInfo.abort.signal,
                    })
                    .then((tileset) => {
                        if (tableInfo.abort.signal.aborted || !this.tableInfos.has(it.tableName)) {
                            return;
                        }

                        tableInfo.externalTrees.set(it.id, tileset.root);
                    })
                    .catch(() => {
                        if (tableInfo.abort.signal.aborted) {
                            return;
                        }

                        const count = this.failedTilesets.get(it.id);

                        if (count === undefined) {
                            this.failedTilesets.set(it.id, 1);
                        } else {
                            this.failedTilesets.set(it.id, count + 1);
                        }
                    })
                    .finally(() => {
                        this.currentlyFetching--;
                        this.loadTiles();
                    });
            }
        }

        this.updateStats();
    }

    private getDatasetId(tableName: string) {
        for (const [id, item] of Object.entries(this.app.state.datasets)) {
            if (item.kind === "pointcloud" && item.name === tableName) return id;
        }

        throw new Error("Dataset id not found");
    }

    private async loadTableInfo(tableName: string) {
        if (this.startedTables.has(tableName)) {
            return;
        }

        this.startedTables.add(tableName);

        const abort = new AbortController();
        this.tableAborts.set(tableName, abort);

        try {
            await this.fetchTableInfo(tableName, abort);
        } catch (e) {
            if (this.tableAborts.get(tableName) === abort) {
                this.tableAborts.delete(tableName);
                this.startedTables.delete(tableName);
            }

            throw e;
        }
    }

    private async fetchTableInfo(tableName: string, abort: AbortController) {
        const tileset = await this.lumidb.get3DTileset({
            tableName,
            queryBoundary: null,
            sourceFileFilter: null,
            classFilter: null,
            output_format: "json",
            outputProj: this.app.state.proj,
            abortSignal: abort.signal,
        });

        const datasetId = this.getDatasetId(tableName);
        const datasetColor = getCachedColor(datasetId);
        const material = new CustomPointMaterial({ datasetColor });

        const {
            points,
            pointCount,
            offset: tableOffset,
        } = await PointChunk.loadTile(this.lumidb, tableName, tileset.root.content, material, abort.signal).catch(
            (e) => {
                material.dispose();
                throw e;
            },
        );

        // The table might have been unloaded during the initial fetches
        if (this.tableAborts.get(tableName) !== abort) {
            points.geometry.dispose();
            material.dispose();
            return;
        }

        positionPoints(points);

        const root: LoadedTile = {
            id: tableName,
            tableName: tableName,
            node: tileset.root,
            bounds: getBvolBox(tileset.root.boundingVolume.region, this.app.state.offset),
            depth: 0,
            children: null,
            data: {
                pointCount: pointCount,
                points: points,
            },
        };

        this.loadedTiles.add(root);

        const tablePosition = tableOffset.clone().sub(this.app.state.offset);

        const tableInfo: TableInfo = {
            datasetId: datasetId,
            tableName: tableName,
            proj: this.app.state.proj,
            root: root,
            contentBounds: new THREE.Box3(),
            tableGroup: new THREE.Group(),
            octreeBounds: new THREE.Box3(),
            externalTrees: new Map(),
            material: material,
            abort: abort,
            position: tablePosition,
        };

        tableInfo.tableGroup.position.copy(tableInfo.position);
        tableInfo.tableGroup.add(root.data.points);
        this.group.add(tableInfo.tableGroup);
        this.tableInfos.set(tableName, tableInfo);
        this.state.tables++;
    }

    private hideTile(it: TileInfo) {
        if (it.data) it.data.points.visible = false;
        if (it.children) {
            for (const child of it.children) {
                this.hideTile(child);
            }
        }
    }

    private sweep() {
        const budget = this.getBudget();
        const pointsInMemory = this.loadedTiles.values().reduce((acc, b) => acc + b.data.pointCount, 0);

        if (pointsInMemory <= budget.memory) {
            return;
        }

        const q = new PriorityQueue<LoadedTile>((it) => this.getTileScreenSize(it));
        q.initialize(this.loadedTiles);

        let pts = 0;

        while (true) {
            const it = q.dequeue();

            if (!it) {
                break;
            }

            if (!it.data.points.visible && pts + it.data.pointCount > budget.memory) {
                this.unloadTile(it);
            } else {
                pts += it.data.pointCount;
            }
        }
    }

    private isLoadedTile(tile: TileInfo): tile is LoadedTile {
        return tile.data !== null;
    }

    private unloadTile(tile: TileInfo) {
        if (tile.data) {
            if (this.isLoadedTile(tile)) {
                this.loadedTiles.delete(tile);
            }

            const tableInfo = this.tableInfos.get(tile.tableName);

            if (tableInfo) {
                tableInfo.tableGroup.remove(tile.data.points);
            }

            tile.data.points.geometry.dispose();
            tile.data = null;
        }

        this.queuedTiles.delete(tile.id);
    }
    private unloadLabels(tableName: string) {
        for (const [key, label] of this.labelCache) {
            if (key.split("\0").includes(tableName)) {
                this.labelCache.delete(key);
                this.labels.remove(label);
            }
        }
    }

    public unloadTables() {
        for (const tableName of this.tableAborts.keys()) {
            this.unloadTable(tableName);
        }
    }

    private unloadTable(tableName: string) {
        const controller = this.tableAborts.get(tableName);

        if (controller) {
            controller.abort();
            this.tableAborts.delete(tableName);
        }

        this.startedTables.delete(tableName);

        const table = this.tableInfos.get(tableName);

        if (table) {
            this.loadQueue.clear();
            this.queuedTiles.clear();

            const stack: TileInfo[] = [table.root];
            while (true) {
                const it = stack.pop();
                if (!it) break;
                this.unloadTile(it);
                if (it.children) stack.push(...it.children);
            }

            table.material.dispose();
            this.group.remove(table.tableGroup);
            this.tableInfos.delete(tableName);
            this.state.tables--;
        }

        this.unloadLabels(tableName);
    }

    private getBudget() {
        if (this.context instanceof CrossSection) {
            return BUDGETS.crossSection;
        }

        const quality = this.prefs.state.quality;
        const budget = BUDGETS[quality];

        return budget;
    }

    private getLabelElement(tableName: string) {
        const datasetId = this.getDatasetId(tableName);
        const color = getCachedColor(datasetId);
        const container = document.createElement("div");
        container.className =
            "bg-white dark:bg-gray-70 dark:text-white px-2 py-1.5 rounded-md text-xs shadow flex gap-2 items-center";
        const box = document.createElement("div");
        box.className = `w-4 h-4 rounded`;
        box.style.backgroundColor = `rgb(${color[0]}, ${color[1]}, ${color[2]})`;
        container.appendChild(box);
        const text = document.createElement("div");
        text.className = "text-xs font-medium";
        text.textContent = getDisplayName(this.app.state.datasets, datasetId);
        container.appendChild(text);
        return container;
    }

    private getLabel(label: Label) {
        const container = document.createElement("div");
        container.className = "flex flex-col gap-1 css2d-label";

        if (label.tableName.length <= MAX_LABEL_COUNT) {
            for (let i = 0; i < label.tableName.length; i++) {
                const element = this.getLabelElement(label.tableName[i]);
                container.appendChild(element);
            }
        } else {
            const element = document.createElement("div");
            element.className =
                "bg-white dark:bg-gray-70 dark:text-white px-2 py-1.5 rounded-md text-xs shadow flex gap-1 items-center";
            const icon = document.createElement("div");
            icon.innerHTML = PIN_SVG;
            element.appendChild(icon);
            const text = document.createElement("div");
            text.textContent = `${label.tableName.length} datasets`;
            text.className = "text-xs font-medium";
            element.appendChild(text);
            container.appendChild(element);
        }

        const l = new CSS2DObject(container);
        l.layers.set(LAYERS.annotations);
        return l;
    }

    private async updateTables() {
        const visible = this.app.state.visible;

        for (const tableName of this.tableInfos.keys()) {
            const tableInfo = this.tableInfos.get(tableName);

            if (!tableInfo) {
                continue;
            }

            const isVisible = visible.includes(tableInfo.datasetId);
            const projMismatch = tableInfo?.proj !== this.app.state.proj;

            if (!isVisible || projMismatch) {
                this.unloadTable(tableName);
            }
        }

        await Promise.allSettled(
            visible.map((datasetId) => {
                const dataset = this.app.state.datasets[datasetId];
                if (dataset?.kind !== "pointcloud") return Promise.resolve();
                return this.loadTableInfo(dataset.name);
            }),
        );
    }

    private updateFrustum() {
        this.context.camera.updateMatrixWorld(true);
        this.temp.projectionMatrix.multiplyMatrices(
            this.context.camera.projectionMatrix,
            this.context.camera.matrixWorldInverse,
        );
        this.temp.frustum.setFromProjectionMatrix(this.temp.projectionMatrix);
    }

    private partitionTables(): { tiles: LoadedTile[]; labels: Label[] } {
        const tiles: LoadedTile[] = [];
        const labelCandidates: TableInfo[] = [];
        const visible = this.app.state.visible;

        for (const tableInfo of this.tableInfos.values()) {
            const tileSize = this.getTileScreenSize(tableInfo.root);

            if (visible.includes(tableInfo.datasetId)) {
                // Always show tiles in the cross section
                if (tileSize >= MIN_ROOT_TILE_SIZE || this.context instanceof CrossSection) {
                    tiles.push(tableInfo.root);
                } else {
                    this.hideTile(tableInfo.root);

                    if (this.temp.frustum.intersectsBox(tableInfo.root.bounds)) {
                        labelCandidates.push(tableInfo);
                    }
                }
            } else {
                this.hideTile(tableInfo.root);
            }
        }

        const labels: Label[] = labelCandidates.map((tableInfo) => ({
            tableName: [tableInfo.tableName],
            position: tableInfo.position.clone(),
            screenPos: new THREE.Vector2(),
            proj: tableInfo.proj,
        }));

        return { tiles, labels };
    }

    private updateLabels(labels: Label[]) {
        const projected = this.temp.clampedPoint;

        for (const l of labels) {
            projected.copy(l.position).project(this.context.camera);
            const sx = (projected.x * 0.5 + 0.5) * this.context.size.width;
            const sy = (1 - (projected.y * 0.5 + 0.5)) * this.context.size.height;
            l.screenPos.set(sx, sy);
        }

        for (let i = 0; i < labels.length; i++) {
            for (let j = labels.length - 1; j > i; j--) {
                const c1 = labels[i];
                const c2 = labels[j];
                const sx1 = c1.screenPos.x;
                const sy1 = c1.screenPos.y;
                const sx2 = c2.screenPos.x;
                const sy2 = c2.screenPos.y;
                const hypot = Math.hypot(sx1 - sx2, sy1 - sy2);

                const w1 = LABEL_PADDING + Math.max(...c1.tableName.map((n) => n.length)) * LABEL_CHAR_WIDTH;
                const w2 = LABEL_PADDING + Math.max(...c2.tableName.map((n) => n.length)) * LABEL_CHAR_WIDTH;
                const mergeRadius = (w1 + w2) / 2;

                if (hypot < mergeRadius) {
                    c1.tableName = [...c1.tableName, ...c2.tableName];
                    labels.splice(j, 1);
                }
            }
        }

        const activeKeys = new Set<string>();

        for (const l of labels) {
            const key = l.tableName.slice().sort().join("\0");
            activeKeys.add(key);

            let label = this.labelCache.get(key);

            if (!label) {
                label = this.getLabel(l);
                this.labelCache.set(key, label);
                this.labels.add(label);
            }

            label.position.copy(l.position);
            label.visible = true;
        }

        for (const [key, label] of this.labelCache) {
            if (!activeKeys.has(key)) {
                label.visible = false;
            }
        }
    }

    private canHidetile(tile: TileInfo): boolean {
        const intersectsFrustum = this.temp.frustum.intersectsBox(tile.bounds);
        if (!intersectsFrustum) return true;

        const isCrossSection = this.context instanceof CrossSection;
        const minDepth = isCrossSection ? CROSS_SECTION_MIN_DEPTH : 0;
        if (tile.depth <= minDepth) return false;

        const tileSize = this.getTileScreenSize(tile);
        const budget = this.getBudget();
        if (tileSize >= budget.minTileSize) return false;

        return true;
    }

    private walkTiles(roots: LoadedTile[]) {
        let pointsVisible = 0;
        const budget = this.getBudget();
        this.walkQueue.initialize(roots);

        while (true) {
            const it = this.walkQueue.dequeue();

            if (!it) break;

            if (this.canHidetile(it)) {
                this.hideTile(it);
                continue;
            }

            const failCount = this.failedTiles.get(it.id) || 0;

            if (it.data) {
                if (pointsVisible + it.data.pointCount <= budget.rendering) {
                    it.data.points.visible = true;
                    pointsVisible += it.data.pointCount;
                } else {
                    it.data.points.visible = false;
                }
            } else if (!this.queuedTiles.has(it.id) && failCount < RETRY_LIMIT) {
                this.queuedTiles.add(it.id);
                this.loadQueue.enqueue(it);
            }

            if (!it.children) {
                if (it.node.children) {
                    it.children = [];
                    for (const childNode of it.node.children) {
                        it.children.push({
                            id: childNode.tileId,
                            tableName: it.tableName,
                            node: childNode,
                            bounds: getBvolBox(childNode.boundingVolume.region, this.app.state.offset),
                            depth: it.depth + 1,
                            children: null,
                            data: null,
                        });
                    }
                } else {
                    // node having no children means it's a external tileset reference
                    const tableInfo = this.tableInfos.get(it.tableName);

                    if (!tableInfo) {
                        continue;
                    }

                    const subtree = tableInfo.externalTrees.get(it.id);

                    if (subtree) {
                        // replace the current node with the root of the external tileset
                        tableInfo.externalTrees.delete(it.id);
                        const extRoot = this.getTileInfo(tableInfo, subtree, it.depth);
                        Object.assign(it, extRoot);

                        if (!it.children && subtree.children) {
                            it.children = subtree.children.map((childNode) =>
                                this.getTileInfo(tableInfo, childNode, it.depth + 1),
                            );
                        }
                    }
                }
            }

            if (it.children) {
                for (const child of it.children) {
                    this.walkQueue.enqueue(child);
                }
            }
        }
    }

    private async doEval() {
        this.evalAt = performance.now();

        await this.updateTables();
        this.updateFrustum();

        const { tiles, labels } = this.partitionTables();

        this.updateLabels(labels);
        this.walkTiles(tiles);
        this.loadTiles();

        this.context.update();
    }
}
